import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { useApp } from "../context/AppContext";
import { SERVICE_LOCATION, GET_CLIENT_ACCOUNTS, CREATE_CLIENT_LOGIN } from "../constants/endpoints";

const CreateClientLogin = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading, showToast } = useApp();

    const { data } = location.state || {};

    // ── Form state ─────────────────────────────────────────────
    const [clientAccounts, setClientAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [userName, setUserName] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [userPhone, setUserPhone] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [successInfo, setSuccessInfo] = useState(null);
    const [errorInfo, setErrorInfo] = useState("");

    // ── Fetch client accounts ────────────────────
    const fetchClientAccounts = useCallback(() => {
        setLoading(true);
        axios
            .get(
                SERVICE_LOCATION + GET_CLIENT_ACCOUNTS,
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            )
            .then(res => {
                if (res.data.isSuccess) {
                    setClientAccounts(res.data.data.clientAccounts || []);
                } else {
                    showToast("error", res.data.errorMessage || "Failed to load client accounts.");
                }
            })
            .catch(() => showToast("error", "Network error. Please try again."))
            .finally(() => setLoading(false));
    }, [data, setLoading, showToast]);

    useEffect(() => { fetchClientAccounts(); }, [fetchClientAccounts]);

    // ── Validation ────────────────────────────────────────────
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!selectedAccount) e.clientAccount = "Please select a client account.";
        if (!userName.trim()) e.userName = "Username is required.";
        if (!displayName.trim()) e.displayName = "Contact name is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────
    const handleCreate = async () => {
        setSuccessInfo(null);
        setErrorInfo("");
        if (!validate()) return;

        setLoading(true);
        try {
            const res = await axios.post(
                SERVICE_LOCATION + CREATE_CLIENT_LOGIN,
                {
                    clientAccount: selectedAccount.clientAccount,
                    userName: userName.trim().toUpperCase(),
                    userDisplayName: displayName.trim(),
                    userPhone: userPhone.trim() || "",
                    userEmail: userEmail.trim() || "",
                },
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            );
            setLoading(false);
            if (res.data.isSuccess) {
                setSuccessInfo({
                    userName: userName.trim(),
                    message: res.data.data.message,
                });
                setSelectedAccount(null);
                setUserName("");
                setDisplayName("");
                setUserPhone("");
                setUserEmail("");
                setErrors({});
            } else {
                setErrorInfo(res.data.errorMessage || "Failed to create login.");
            }
        } catch {
            setLoading(false);
            showToast("error", "Network error. Please try again.");
        }
    };

    const handleCancel = () => {
        navigate("/home/admin", { state: { data } });
    };

    // ── Dropdown option template ──────────────────────────────
    const optionTemplate = (option) => (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{option.clientName}</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#9CA3AF" }}>{option.clientAccount}</span>
        </div>
    );

    return (
        <div className="page-enter" style={{ maxWidth: 640 }}>
            <h1 style={styles.heading}>Create Client Login</h1>
            <p style={styles.sub}>Set up a new login for a client account.</p>

            {/* Success banner */}
            {successInfo && (
                <div style={styles.successBanner}>
                    <i className="pi pi-check-circle" style={{ fontSize: 18, color: "#4CAF50" }} />
                    <div>
                        <div style={styles.successTitle}>Login created successfully</div>
                        <div style={styles.successDetail}>
                            Username: <strong>{successInfo.userName}</strong>
                        </div>
                    </div>
                </div>
            )}

            {/* Error banner */}
            {errorInfo && (
                <div style={styles.errorBanner}>
                    <i className="pi pi-exclamation-circle" style={{ fontSize: 13, flexShrink: 0 }} />
                    {errorInfo}
                </div>
            )}

            <div style={styles.card}>
                <p style={styles.sectionTitle}>Account Details</p>

                {/* Client Account */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>
                        Client Account <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <div style={styles.fieldInput}>
                        <Dropdown
                            value={selectedAccount}
                            options={clientAccounts}
                            onChange={e => {
                                setSelectedAccount(e.value);
                                setErrors(prev => ({ ...prev, clientAccount: undefined }));
                            }}
                            optionLabel="clientName"
                            itemTemplate={optionTemplate}
                            placeholder="Select a client…"
                            filter
                            filterPlaceholder="Search clients…"
                            style={{
                                ...styles.input,
                                height: 38,
                                border: errors.clientAccount ? "1px solid #EF4444" : "1px solid #D1D5DB",
                            }}
                            panelStyle={{ fontFamily: "var(--font-sans)", fontSize: 13 }}
                        />
                        {errors.clientAccount && <small style={styles.errorText}>{errors.clientAccount}</small>}
                    </div>
                </div>

                {/* Username */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>
                        Username <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userName}
                            onChange={e => {
                                setUserName(e.target.value);
                                setErrors(prev => ({ ...prev, userName: undefined }));
                            }}
                            placeholder="Enter username"
                            style={{
                                ...styles.input,
                                border: (errors.userName) ? "1px solid #EF4444" : "1px solid #D1D5DB",
                            }}
                        />
                        {errors.userName && <small style={styles.errorText}>{errors.userName}</small>}
                    </div>
                </div>

                {/* Divider */}
                <div style={{ borderTop: "1px solid #F3F4F6", margin: "16px 0" }}>
                    <span style={styles.dividerLabel}>Contact Details</span>
                </div>

                {/* Displayname */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>
                        Contact Name <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={displayName}
                            onChange={e => {
                                setDisplayName(e.target.value);
                                setErrors(prev => ({ ...prev, displayName: undefined }));
                            }}
                            placeholder="Enter contact name"
                            style={{
                                ...styles.input,
                                border: (errors.displayName) ? "1px solid #EF4444" : "1px solid #D1D5DB",
                            }}
                        />
                        {errors.displayName && <small style={styles.errorText}>{errors.displayName}</small>}
                    </div>
                </div>

                {/* Phone (optional) */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Phone</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userPhone}
                            onChange={e => setUserPhone(e.target.value)}
                            placeholder="07… (optional)"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Email (optional) */}
                <div style={styles.fieldRow}>
                    <label style={styles.fieldLabel}>Email</label>
                    <div style={styles.fieldInput}>
                        <InputText
                            value={userEmail}
                            onChange={e => setUserEmail(e.target.value)}
                            placeholder="email@example.com (optional)"
                            keyfilter="email"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Default password note */}
                <div style={styles.infoNote}>
                    <i className="pi pi-info-circle" style={{ fontSize: 13, color: "#3B82F6", flexShrink: 0 }} />
                    <span>A default password will be assigned automatically by the system.</span>
                </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    onClick={handleCancel}
                    style={styles.btnSecondary}
                />
                <Button
                    label="Create Login"
                    icon="pi pi-user-plus"
                    onClick={handleCreate}
                    style={styles.btnPrimary}
                />
            </div>
        </div>
    );
};

const styles = {
    heading: {
        fontFamily: "var(--font-sans)",
        fontSize: 22,
        fontWeight: 700,
        color: "#111827",
        margin: "0 0 6px 0",
    },
    sub: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        color: "#6B7280",
        margin: "0 0 24px 0",
    },
    card: {
        background: "#ffffff",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "28px 32px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    sectionTitle: {
        fontFamily: "var(--font-sans)",
        fontSize: 11,
        fontWeight: 600,
        color: "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        margin: "0 0 16px 0",
    },
    dividerLabel: {
        display: "inline-block",
        fontFamily: "var(--font-sans)",
        fontSize: 11,
        fontWeight: 600,
        color: "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        background: "#ffffff",
        position: "relative",
        top: -10,
        left: 8,
    },
    fieldRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        marginBottom: 16,
    },
    fieldLabel: {
        fontFamily: "var(--font-sans)",
        width: 150,
        minWidth: 130,
        fontSize: 13,
        fontWeight: 500,
        color: "#4B5563",
        flexShrink: 0,
        paddingTop: 9,
    },
    fieldInput: {
        flex: 1
    },
    input: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        border: "1px solid #D1D5DB",
        width: "100%",
        color: "#111827",
        padding: "0 12px",
    },
    errorText: {
        display: "block",
        marginTop: 4,
        fontSize: 12,
        color: "#EF4444",
        fontFamily: "var(--font-sans)",
    },
    errorBanner: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 8,
        marginBottom: 10,
        padding: "8px 12px",
        background: "#FEE2E2",
        border: "1px solid #FECACA",
        borderRadius: 6,
        fontSize: 13,
        color: "#991B1B",
        fontFamily: "var(--font-sans)",
        fontWeight: 500,
    },
    infoNote: {
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginTop: 8,
        padding: "10px 14px",
        background: "#EFF6FF",
        border: "1px solid #BFDBFE",
        borderRadius: 8,
        fontSize: 12,
        color: "#1E40AF",
        fontFamily: "var(--font-sans)",
        lineHeight: 1.5,
    },
    successBanner: {
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        marginBottom: 20,
        padding: "16px 20px",
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: 10,
    },
    successTitle: {
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        fontWeight: 600,
        color: "#166534",
        marginBottom: 4,
    },
    successDetail: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        color: "#166534",
        lineHeight: 1.6,
    },
    btnSecondary: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        borderColor: "#D1D5DB",
        color: "#374151",
    },
    btnPrimary: {
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        height: 38,
        borderRadius: 6,
        background: "#4CAF50",
        borderColor: "#4CAF50",
        color: "#ffffff",
    },
};

export default CreateClientLogin;
